import {
  CardDataInter,
  CustomProps,
  GameCardCategory,
  generateCustomProps
} from '../../../common.type'
import { GameFavorites } from '@/views/game/classes'
import { GamePlatformCatgory } from '@/views/game/consts/categorys'
import { PlatformCategories } from '@/api/game/type'
import {
  PlatformCategoriesItem,
  PlatformCategoriesItemInter
} from '@/service/game/entity/gameCategorie'
import { SelfOpertedId } from '@/views/game/consts/platforms'
import { SquareProps } from '.'
import { gameOpener } from '@/views/game/services'
import { useGameStore } from '@/views/game/store'
import AnchorHot, { AnchorHotInter } from '@/service/game/entity/anchorHot'
import GameItem, { GameItemInter } from '@/service/game/entity/gameItem'

/** 转换Props类型声明 */
export interface GetProps<T extends CardDataInter = CardDataInter> {
  <Data extends T = T>(
    data: Data,
    custom?: CustomProps<Data, SquareProps>
  ): SquareProps
}

/** 方型卡片 获得props */
export const getProps: GetProps = (data, custom) => {
  if (PlatformCategoriesItem.isThisType(data)) {
    return getPropsByPlatformCategoriesItem(data, custom)
  } else if (GameItem.isThisType(data)) {
    return getPropsByGameItem(data, custom)
  } else if (AnchorHot.isThisType(data)) {
    return getPropsByAnchorHot(data, custom)
  } else {
    return getPropsByPlatformCategories(
      data as PlatformCategories,
      custom as CustomProps<PlatformCategories, SquareProps>
    )
  }
}

/** 根据游戏项 */
const getPropsByGameItem: GetProps<GameItemInter> = (data, custom?) => {
  const gameStore = useGameStore()
  /** 生成与收藏相关 */
  const generateFavoritedRelation = () => {
    const { gameId, maintainStatus } = data
    const { currentAllFavoriteList } = gameStore
    const isShowFavorited = Boolean(
      gameId && !!currentAllFavoriteList && !maintainStatus
    )
    const isFavorited = currentAllFavoriteList?.includes(gameId)
    return { isShowFavorited, isFavorited }
  }
  /** 生成logo(大多灵敏情况都不显示logo,特殊才显示) */
  const generateLogoUrl = () => {
    // 如果不是自营平台,不展示logo图片
    if (
      data.platformId !== SelfOpertedId &&
      !GamePlatformCatgory.includes(data.gameCategoryId)
    ) {
      return ''
    }
    // 如果是三合一,合成图,自带logo的,或自定义整图不展示log
    if (data.isCombinate || data.isCustomIcon) {
      return ''
    }
    return data.platformLogo
  }
  const { isShowFavorited, isFavorited } = generateFavoritedRelation()
  const props: SquareProps = {
    imgUrl: data.gameIcon,
    isCompleteImage: true,
    inCardRenderCategory: GameCardCategory.Game,
    isCustomIcon: data.isCustomIcon,
    logoUrl: generateLogoUrl(),
    gameName: data.gameName,
    status: data.recommendStatus ? 'remenber' : undefined,
    isMaintain: data.maintainStatus,
    isShowFavorited,
    isFavorited,
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  }
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}

/** 根据主播游戏项 */
const getPropsByAnchorHot: GetProps<AnchorHotInter> = (data, custom?) => {
  const props: SquareProps = {
    inCardRenderCategory: GameCardCategory.Game,
    imgUrl: data.gameIcon,
    gameName: data.gameName,
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  }
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}

/** 根据平台类型生成配置项
 * 这种平台卡片有收藏功能的
 * 版式7,DQ定制的在使用型卡片渲染平台
 */
const getPropsByPlatformCategoriesItem: GetProps<
  PlatformCategoriesItemInter
> = (data, custom?) => {
  const gameStore = useGameStore()
  /** 生成与收藏相关 */
  const generateFavoritedRelation = () => {
    const { gameId, maintainStatus } = data
    const { currentAllFavoriteList } = gameStore
    const isShowFavorited = Boolean(
      gameId && !!currentAllFavoriteList && !maintainStatus
    )
    const isFavorited = currentAllFavoriteList?.includes(gameId)
    return { isShowFavorited, isFavorited }
  }
  const { isShowFavorited, isFavorited } = generateFavoritedRelation()
  /** props */
  const props: SquareProps = {
    imgUrl: data.platformIcon,
    isCompleteImage: true,
    inCardRenderCategory: GameCardCategory.Platform,
    isCustomIcon: data.isCustomIcon,
    gameName: data.brand,
    logoUrl: data.platformLogo,
    isMaintain: data.maintainStatus,
    isShowFavorited,
    isFavorited,
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  }
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}

/** 旧版的数据结构 */
const getPropsByPlatformCategories: GetProps<PlatformCategories> = (
  data,
  custom?
) => {
  const gameStore = useGameStore()
  /** 生成图片相关 */
  const generateImgRelation = () => {
    const ret = {} as Pick<SquareProps, 'imgUrl' | 'isCustomIcon'>
    const { gameIconLarge, largeIcon, gameIcon } = data
    // 如果是用户自定义整图,则直接返回用户自定义图
    if (Boolean(data.customLargeIcon)) {
      ret.imgUrl = gameIcon || gameIconLarge || largeIcon
      ret.isCustomIcon = true
    }
    // 如果是后台形成的合同,则直接返回后台返回的合图,优先级低于用户自定义图
    else if (Boolean(data.isCompositePictureHot)) {
      ret.imgUrl = data.compositePictureHotIcon
      ret.isCustomIcon = true
    }
    // 不是整张合图;
    else {
      ret.imgUrl = gameIconLarge || largeIcon || gameIcon
      ret.isCustomIcon = false
    }
    return ret
  }
  /** 生成 logo 相关 */
  const generateLogoUrl = () => {
    const logoUrl =
      data.platformId === SelfOpertedId ||
      GamePlatformCatgory.includes(data.gameCategoryId) ||
      !data.gameId
        ? data.logo
        : undefined
    return { logoUrl }
  }
  /** 生成与收藏相关 */
  const generateFavoritedRelation = () => {
    const { gameId, maintainStatus } = data
    const { currentAllFavoriteList } = gameStore
    const isShowFavorited = Boolean(
      gameId && !!currentAllFavoriteList && !maintainStatus
    )
    const isFavorited = currentAllFavoriteList?.includes(gameId)
    return { isShowFavorited, isFavorited }
  }
  const { imgUrl, isCustomIcon } = generateImgRelation()
  const { logoUrl } = generateLogoUrl()
  const { isShowFavorited, isFavorited } = generateFavoritedRelation()
  const props = {
    imgUrl,
    isCustomIcon,
    isCompleteImage: true,
    logoUrl,
    gameName: data.gameName || data?.brand || '',
    status: data.recommendStatus ? 'remenber' : undefined,
    isMaintain: data.maintainStatus,
    isShowFavorited,
    isFavorited,
    handleClickCard: () => gameOpener.openGame(data),
    handleClickFavorIcon: () => GameFavorites.collectGame(data)
  } as unknown as SquareProps
  // 自定义放在最后,覆盖前面的
  const customProps = generateCustomProps(data, props, custom)
  return Object.assign(props, customProps)
}
